import {KpiCalculationError} from '@hconnect/common/types'
import {completionScoreBandColor} from '@hconnect/common/utils'
import {Box, CircularProgress, Typography} from '@mui/material'
import {isNil} from 'lodash'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, VALIDATION_TRANSLATIONS_KEY} from '../consts'
import {HierarchyTableNode} from '../types'

type AverageCellConfig = {
  renderActions?: (item: HierarchyTableNode) => React.ReactNode
}

export const useRenderAverageCell = ({renderActions}: AverageCellConfig = {}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const getAverageDisplayValue = useCallback(
    (item: HierarchyTableNode) => {
      if (item.averageError) {
        return item.averageError === KpiCalculationError.NoKpiValue
          ? '-'
          : t(VALIDATION_TRANSLATIONS_KEY[item.averageError])
      }
      if (item.average !== undefined) return `${Math.round(item.average)}%`
      return '-'
    },
    [t]
  )

  const getAverageColor = (item: HierarchyTableNode) => {
    if (item.averageIsDirty) return 'text.disabled'
    return isNil(item.average) ? 'initial' : completionScoreBandColor(item.average)
  }

  return useCallback(
    (item: HierarchyTableNode) => (
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <Box display="inline-flex" alignItems="center" gap={0.5}>
          <Typography
            component="span"
            display="inline-flex"
            alignItems="center"
            variant="h4"
            sx={{color: getAverageColor(item)}}
          >
            {getAverageDisplayValue(item)}
          </Typography>
          {item.averageIsDirty && <CircularProgress size={16} />}
        </Box>
        {renderActions?.(item)}
      </Box>
    ),
    [getAverageDisplayValue, renderActions]
  )
}
